// Ask Bizminer (CHAT-12) panel state: is the entry point visible, is the panel
// open, and which report (if any) it was opened about. The conversation itself
// lives in the panel component (components/AskBizminer/AskBizminerPanel.vue).
import { chatConfigured, postInsights, withReauth, isDenied } from '@/components/AskBizminer/chatApi';
import { watchChatFlag } from '@/components/AskBizminer/chatFlag';
import { computeChatVisible, isDeniedFor, chatMode, teaserConfirmed, normalizeInsights, insightsErrorView, insightsUnverified, shouldRequestInsights, INSIGHTS_UNVERIFIED_VIEW, entitlementFromError } from '@/components/AskBizminer/chatCore';

let unwatchFlag = null;

const state = {
  flag: false,
  open: false,
  context: null,
  // Bumped on every openPanel() so the panel re-handshakes on each open, even
  // when it is already open (a second "Ask about this report" click).
  openSeq: 0,
  // uid the chat service / api.app refused (not_in_beta, not_allowed). Kept for
  // the whole session: it ends on logout (RESET) or when another user signs in.
  deniedUid: null,
  // What the chat service said about this user's subscription (CHAT-18):
  // 'subscriber' | 'teaser' | null (not asked yet), for the uid it was read for.
  // `retryable` = the teaser was a fail-closed result ("Try again").
  entitlement: null,
  entitlementUid: null,
  entitlementRetryable: false,
  // A question to send as soon as the panel is connected ("Ask about this" on an
  // insight). Taken (and cleared) by the panel.
  pendingQuestion: '',
  // "What stands out?" results (CHAT-20), by report id, so moving between the
  // report's tabs does not ask again. {phase: 'loading'|'done'|'error', data, view}.
  // `insightsEpoch` bumps on logout so a late answer is never shown to the next user.
  insights: {},
  insightsEpoch: 0,
};

// The server's answer, only for the user it was read for.
const serverEntitlement = (state, user) =>
  state.entitlement && user && user.userID != null && String(user.userID) === String(state.entitlementUid) ? state.entitlement : null;

const getters = {
  // The ONE visibility rule behind every entry point (top-bar button, report
  // pill, the panel itself). See computeChatVisible in chatCore.js.
  visible: (state, getters, rootState) =>
    computeChatVisible({
      flag: state.flag,
      denied: isDeniedFor(state.deniedUid, rootState.user && rootState.user.user),
      configured: chatConfigured(),
      user: rootState.user && rootState.user.user,
      userType: rootState.user && rootState.user.user_type,
    }),
  isOpen: (state, getters) => state.open && getters.visible,
  // 'full' = the chat panel, 'teaser' = the locked panel (retail, academic,
  // lapsed). The client guess is replaced by the server's entitlement as soon as
  // the panel has read it. See chatMode in chatCore.js.
  mode: (state, getters, rootState) => {
    const user = rootState.user && rootState.user.user;
    return chatMode({
      userType: rootState.user && rootState.user.user_type,
      user,
      serverEntitlement: serverEntitlement(state, user),
    });
  },
  // The teaser is a fact only once the server said so (or for an academic
  // session): until then a client guess of "teaser" shows no pitch (P2).
  teaserConfirmed: (state, getters, rootState) => {
    const user = rootState.user && rootState.user.user;
    return getters.mode === 'teaser' && teaserConfirmed({ userType: rootState.user && rootState.user.user_type, serverEntitlement: serverEntitlement(state, user) });
  },
  // The lock on the top-bar button: a confirmed teaser only.
  locked: (state, getters) => getters.teaserConfirmed,
  teaserRetryable: (state, getters, rootState) =>
    getters.mode === 'teaser' && state.entitlementRetryable && serverEntitlement(state, rootState.user && rootState.user.user) === 'teaser',
};

const mutations = {
  SET_FLAG(state, v) {
    state.flag = !!v;
  },
  OPEN(state, context) {
    state.open = true;
    state.context = context || null;
    state.pendingQuestion = '';
    state.openSeq++;
  },
  ASK(state, { context, question }) {
    state.open = true;
    state.context = context || null;
    state.pendingQuestion = question || '';
    state.openSeq++;
  },
  CLEAR_QUESTION(state) {
    state.pendingQuestion = '';
  },
  SET_INSIGHTS(state, { key, value }) {
    state.insights = { ...state.insights, [key]: value };
  },
  CLOSE(state) {
    state.open = false;
  },
  SET_CONTEXT(state, context) {
    state.context = context || null;
  },
  DENIED(state, uid) {
    state.deniedUid = uid != null ? uid : null;
    state.open = false;
  },
  SET_ENTITLEMENT(state, { uid, entitlement, retryable }) {
    state.entitlement = entitlement || null;
    state.entitlementUid = uid != null ? uid : null;
    state.entitlementRetryable = !!retryable;
  },
  RESET(state) {
    state.entitlement = null;
    state.entitlementUid = null;
    state.entitlementRetryable = false;
    state.flag = false;
    state.open = false;
    state.context = null;
    state.deniedUid = null;
    state.pendingQuestion = '';
    state.insights = {};
    state.insightsEpoch++;
  },
};

const actions = {
  // Safe to call repeatedly (every MainNavbar mount): one listener, and the
  // current flag value is always re-read. Never clears `denied`: a remount must
  // not bring the button back after the service said no.
  watchFlag({ commit }) {
    if (unwatchFlag) unwatchFlag();
    unwatchFlag = watchChatFlag((on) => commit('SET_FLAG', on));
  },
  // chat.bizminer.com said not_in_beta, or api.app's assertion said not_allowed.
  deny({ commit, rootState }) {
    const u = rootState.user && rootState.user.user;
    commit('DENIED', u && u.userID != null ? u.userID : null);
  },
  // The chat service's entitlement for the signed-in user (POST /session, GET /me,
  // or a subscription_required answer). The server is authoritative.
  setEntitlement({ commit, rootState }, { entitlement, retryable }) {
    const u = rootState.user && rootState.user.user;
    commit('SET_ENTITLEMENT', { uid: u && u.userID != null ? u.userID : null, entitlement, retryable });
  },
  openPanel({ commit, getters }, context) {
    if (getters.visible) commit('OPEN', context);
  },
  // "Ask about this" on an insight: open the panel on this report and send the question.
  askAbout({ commit, getters }, { context, question }) {
    if (getters.visible) commit('ASK', { context, question });
  },
  // The panel took the question (or could not send it).
  takeQuestion({ commit, state }) {
    const q = state.pendingQuestion;
    commit('CLEAR_QUESTION');
    return q;
  },
  // POST /insights for one report. Only ever dispatched from the "What stands out?"
  // click. A finished or running request for the same report is reused; `force`
  // (Retry) asks again after an error.
  async loadInsights({ commit, dispatch, state, rootState }, { key, request, force }) {
    // A running or finished request is reused; after a daily-limit answer (429
    // insights_limit) the report is locked until the page reloads: no more POSTs.
    if (!shouldRequestInsights(state.insights[key], force)) return;
    const epoch = state.insightsEpoch;
    const uid = rootState.user && rootState.user.user && rootState.user.user.userID;
    const stale = () => epoch !== state.insightsEpoch || String(uid) !== String(rootState.user && rootState.user.user && rootState.user.user.userID);
    commit('SET_INSIGHTS', { key, value: { phase: 'loading', data: null, view: null } });
    try {
      const body = await withReauth(() => postInsights(request));
      if (stale()) return;
      const data = normalizeInsights(body);
      // verified:false = the service could not confirm the report id: show no insights.
      if (insightsUnverified(body)) commit('SET_INSIGHTS', { key, value: { phase: 'error', data: null, view: { ...INSIGHTS_UNVERIFIED_VIEW } } });
      else if (data) commit('SET_INSIGHTS', { key, value: { phase: 'done', data, view: null } });
      else commit('SET_INSIGHTS', { key, value: { phase: 'error', data: null, view: insightsErrorView(null) } });
    } catch (e) {
      if (stale()) return;
      if (isDenied(e)) {
        commit('SET_INSIGHTS', { key, value: { phase: 'error', data: null, view: { kind: 'message', text: '', retryable: false } } });
        dispatch('deny');
        return;
      }
      const view = insightsErrorView(e);
      // The service says the subscription is gone: every entry point follows (locked button, teaser panel).
      if (view.kind === 'locked' || (e && e.code === 'subscription_required')) dispatch('setEntitlement', entitlementFromError(e));
      commit('SET_INSIGHTS', { key, value: { phase: 'error', data: null, view } });
    }
  },
  togglePanel({ commit, getters, state }) {
    if (!getters.visible) return;
    // Opening from the top bar never forces a report: the panel keeps its own
    // conversation (and that conversation's context).
    if (state.open) commit('CLOSE');
    else commit('OPEN', null);
  },
  closePanel({ commit }) {
    commit('CLOSE');
  },
  // Logout or user change.
  reset({ commit }) {
    if (unwatchFlag) {
      unwatchFlag();
      unwatchFlag = null;
    }
    commit('RESET');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
